import { Component, Input, OnInit } from '@angular/core';
import { ContentTypesEnum } from '@enums/content-type.enum';
import { DateFormatEnum } from '@enums/date-format.enum';
import { GenderEnum } from '@enums/gender.enum';
import { PassengerDetails } from '@enums/passenger-details.enum';
import { ExtrasInfo, Passenger, PassengerExtra } from '@services/extras/_interfaces_/extras-info.interface';
import { AirportAssistanceContent, BaggageContent, LoungeContent } from '@shared/services/content/_interfaces_/content.interface';
import { ContentMapperService } from '@shared/services/content/content-mapper.service';
import { ContentService } from '@shared/services/content/content.service';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import {
  AirportAssistanceContentMapped,
  BaggageContentMapped,
  LoungeContentMapped,
} from '@shared/services/content/_interfaces_/content.mapped.interface';
import { ExtrasTypesEnum } from '@enums/extras-types';

@Component({
  selector: 'app-passenger-details-overview',
  templateUrl: './passenger-details.component.html',
  styleUrls: ['./passenger-details.component.scss'],
})
export class PassengerDetailsOverviewComponent implements OnInit {
  @Input() flightDetails: ExtrasInfo;
  @Input() culture: string;
  ddMMyyyy: string = DateFormatEnum.ddMMyyyy;
  selectedPassengers$: Observable<Passenger[]>;
  genderEnum: typeof GenderEnum = GenderEnum;
  contentLounge$: Observable<LoungeContentMapped>;
  contentBaggage$: Observable<BaggageContentMapped>;
  contentAirportAssistance$: Observable<AirportAssistanceContentMapped>;

  constructor(private contentService: ContentService, private contentMapperService: ContentMapperService) {}

  ngOnInit() {
    this.setPassengerExtras();
    this.getExtrasContent();
  }

  public getExtrasContent(): void {
    this.contentLounge$ = this.contentService
      .getContent<LoungeContent>(ContentTypesEnum.myFfExtrasPage)
      .pipe(map((content: LoungeContent) => this.contentMapperService.mapLoungeContent(content)));

    this.contentBaggage$ = this.contentService
      .getContent<BaggageContent>(ContentTypesEnum.myFfExtrasPage)
      .pipe(map((content: BaggageContent) => this.contentMapperService.mapBaggageContent(content)));

    this.contentAirportAssistance$ = this.contentService
      .getContent<AirportAssistanceContent>(ContentTypesEnum.myFfExtrasPage)
      .pipe(map((content: AirportAssistanceContent) => this.contentMapperService.mapAirportAssistanceContent(content)));
  }

  public getContentByServiceType(
    serviceType: ExtrasTypesEnum
  ): Observable<BaggageContentMapped | LoungeContentMapped | AirportAssistanceContentMapped> | null {
    switch (serviceType) {
      case ExtrasTypesEnum.Baggage:
        return this.contentBaggage$;
      case ExtrasTypesEnum.LoungeAccess:
        return this.contentLounge$;
      case ExtrasTypesEnum.MedicalAssistance:
        return this.contentAirportAssistance$;
      default:
        return null;
    }
  }

  public getContentTitle(serviceType: ExtrasTypesEnum, content: unknown): string {
    switch (serviceType) {
      case ExtrasTypesEnum.Baggage:
        return (content as BaggageContentMapped).baggageTitle;
      case ExtrasTypesEnum.LoungeAccess:
        return (content as LoungeContentMapped).loungeTitle;
      case ExtrasTypesEnum.MedicalAssistance:
        return (content as AirportAssistanceContentMapped).airportAssistanceTitle;
      default:
        return '';
    }
  }

  private setPassengerExtras(): void {
    this.selectedPassengers$ = of(this.flightDetails?.passengers).pipe(
      map((passengers: Passenger[]) =>
        passengers?.filter((passenger) => {
          return passenger?.passengerExtras?.some((extra: PassengerExtra) =>
            extra?.options?.some((option) => option?.isSelected && option?.bookingReference !== PassengerDetails.NOBAG)
          );
        })
      ),
      shareReplay(1)
    );
  }
}
